import React, { useState, useEffect } from "react";
import SortBtn from "./SortBtn";
import {
  TableCell,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { Filter } from "react-feather";

/**
 * @param {{
  Children: JSX.Element,
  isSortable: boolean,
  columnName: string,
  width?: string,
  sortedColumn: { column: string, isAsc: boolean },
  onSortColumn: (columnName: string) => void,
  isFilterable?: boolean,
  filteredData?: Set<string>,
  onFilter?: (value: string[]) => void
}} data 
 */
const CustomTableHead = ({
  Children,
  isSortable,
  columnName,
  width,
  sortedColumn,
  onSortColumn,
  isFilterable,
  filteredData,
  onFilter,
}) => {
  const [filters, setFilters] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [processedFilterData, setProcessedFilterData] = useState([]);
  
  // Process the filter data when it changes or when columnName changes
  useEffect(() => {
    if (filteredData && columnName === "provider") {
      // Get unique values
      const values = Array.from(filteredData);
      
      // Split into known providers and others (that will become Datajolt)
      const knownProviders = values.filter(item => 
        item === "AB Hosted" || item === "Fivetran" || item === " ");
      
      const hasOtherProviders = values.some(item => 
        item !== "AB Hosted" && item !== "Fivetran" && item !== " ");
      
      // Create final list with one Datajolt entry if needed
      const finalList = [...knownProviders];
      if (hasOtherProviders) {
        finalList.push("Datajolt");
      }
      
      setProcessedFilterData(finalList);
    } else {
      setProcessedFilterData(Array.from(filteredData || []));
    }
  }, [filteredData, columnName]);

  const handleChange = ({ target: { value } }) => {
    let newValues = [];
    
    if (columnName === "provider" && (value === "Datajolt" || filters.includes("Datajolt"))) {
      // Special handling for Datajolt selection/deselection
      const otherValues = Array.from(filteredData).filter(item => 
        item !== "AB Hosted" && item !== "Fivetran" && item !== " ");
      
      if (value === "Datajolt") {
        // If selecting Datajolt
        if (filters.includes("Datajolt")) {
          // If Datajolt already selected, remove it and all associated values
          newValues = filters.filter(f => 
            !otherValues.includes(f) && f !== "Datajolt");
        } else {
          // If Datajolt not selected, add it and all associated values
          newValues = [...filters, "Datajolt", ...otherValues];
        }
      } else {
        // If selecting/deselecting another value
        const isValueSelected = filters.includes(value);
        
        if (isValueSelected) {
          // Remove the value
          newValues = filters.filter(f => f !== value);
          
          // If removing Datajolt, also remove all associated values
          if (value === "Datajolt") {
            newValues = newValues.filter(f => !otherValues.includes(f));
          }
        } else {
          // Add the value
          newValues = [...filters, value];
        }
      }
    } else {
      // Normal handling for other columns
      const isFilterExist = filters.includes(value);
      
      if (!isFilterExist) {
        newValues = [...filters, value];
      } else {
        newValues = filters.filter((filter) => filter !== value);
      }
    }

    setFilters(newValues);
    onFilter && onFilter(newValues);
  };

  return (
    <TableCell
      sx={{
        width,
        zIndex: 99,
        fontSize: "11px",
        lineHeight: "15px",
        wordBreak: "break-all",
        color: "#448593 !important",
        textTransform: "capitalize",
        minWidth: "70px",
        padding: "0rem 0.7rem",
        borderWidth: 0,
        resize: !width ? "horizontal" : "none",
        overflow: "hidden",
        maxWidth: "300px",
      }}
    >
      <Children>
        {isFilterable && (
          <>
            &nbsp;&nbsp;<Filter
              size={12}
              color={filters.length > 0 ? "#2c49fc" : "#879fb5"}
              strokeWidth={filters.length > 0 ? 2.3 : 2}
              onClick={() => setIsFilterOpen(true)}
            />
            <Select
              value={filters}
              sx={{
                visibility: "hidden",
                opacity: 0,
                width: "0px",
                height: "20px",
              }}
              mutliple="true"
              onChange={handleChange}
              open={isFilterOpen}
              onOpen={() => setIsFilterOpen(true)}
              onClose={() => setIsFilterOpen(false)}
            >
              {processedFilterData.map((item) => (
                  <MenuItem 
                    value={item} 
                    key={item}
                    sx={{
                      padding: '2px 8px 2px 6px',
                      minHeight: '28px',
                      height: 'auto'
                    }}
                  >
                    <Checkbox 
                      size="1" 
                      checked={filters.includes(item)} 
                      sx={{ 
                        padding: '2px 4px 2px 0px',
                      }}
                    />
                    <ListItemText 
                      primary={item === " " ? "(empty)" : item} 
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: "12px", 
                            color: "#879fb5",
                            lineHeight: "18px",
                            fontWeight: "500",
                            fontStyle: item === "" ? "italic" : "normal"
                          }
                        }
                      }}
                    />
                  </MenuItem>
                ))}
            </Select>
          </>
        )}
        {isSortable && (
          <SortBtn
            onClick={() => onSortColumn(columnName)}
            isSorted={
              sortedColumn.column === columnName
                ? sortedColumn.isAsc
                  ? "asc"
                  : "desc"
                : "none"
            }
          />
        )}
      </Children>
    </TableCell>
  );
};

export default CustomTableHead;
